import type { TFunction } from "i18next";
import { useState } from "react";
import type { useU2000FormStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import type { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { U3121ProjectContact, U3121ProjectContactWithSource } from "../../../../U3121Service";
import { U2000FormStateEnumEditMode, U2000FormStateEnumState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { generateTemporaryId } from "../../../../../u2000Core/tools/U2000IdUtils";
import type { U3121Project } from "../../../../U3121Service";
import { U3121_PROJECT_CONTACT_SOURCE_PROJECT } from "./u3121ProjectContactUtils";

export interface U3121ProjectFormExtendedHandlers {
  getData: () => U3121Project | null;
  setData: (data: U3121Project) => void;
  getFormState?: () => U2000FormStateEnumState;
  getOtherErrors?: () => Record<string, unknown> | undefined;
}

export interface U3121ProjectContactHandlerReturn {
  onAdd: () => void;
  onModify: () => void;
  onDelete: () => void;
  onRowClick: (row: U3121ProjectContact) => void;
  onDlgClose: (result: string) => void;
  isDialogOpen: boolean;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isDeleteDialogOpen: boolean;
  onDlgDeleteClose: (result: string) => void;
  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U3121ProjectContact>>;
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U3121ProjectContactWithSource>>;
  dataChangedEvent: number;
}

export interface U3121ProjectContactHandlerParam {
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U3121ProjectContactWithSource>>;
  t: TFunction;
  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U3121ProjectContact>>;
  parentFormHandlers: U3121ProjectFormExtendedHandlers;
}

export function u3121ProjectContactHandler(params: U3121ProjectContactHandlerParam): U3121ProjectContactHandlerReturn {
  const { gridStateHandlers, formStateHandlers, parentFormHandlers } = params;
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [dataChangedEvent, setDataChangedEvent] = useState<number>(0);

  const onAdd = (): void => {
    formStateHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
    setDialogOpen(true);
  };

  const onModify = (): void => {
    const selectedRow = gridStateHandlers.getSelectedRows()[0];
    formStateHandlers.setData(selectedRow as U3121ProjectContact);
    formStateHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    setDialogOpen(true);
  };

  const onDelete = (): void => {
    setDeleteDialogOpen(true);
  };

  const onRowClick = (row: U3121ProjectContact): void => {
    formStateHandlers.setData(row);
    const parentFormState = parentFormHandlers.getFormState?.();
    if (parentFormState === U2000FormStateEnumState.EDIT) {
      formStateHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    } else {
      formStateHandlers.setEditMode(U2000FormStateEnumEditMode.NONE);
    }
    setDialogOpen(true);
  };

  const onDlgClose = (result: string): void => {
    if (result === "cancel") {
      gridStateHandlers.setErrors([]);
      formStateHandlers.cancelData();
      setDialogOpen(false);
    } else if (result === "save") {
      const businessHandlers = formStateHandlers.getBusinessHandlers();
      if (businessHandlers?.validateForm) {
        const errors = businessHandlers.validateForm(formStateHandlers.getData());
        formStateHandlers.setErrors(errors);
        formStateHandlers.saveData();
        if (Object.keys(errors).length > 0) return;
      }
      if (formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.EDIT) {
        const saved = formStateHandlers.getData();
        const updatedData = gridStateHandlers.getData().map((item) => {
          const original = item as U3121ProjectContactWithSource;
          return item.id === saved.id
            ? { ...saved, source: original.source, sourceLabelFr: original.sourceLabelFr, sourceLabelEn: original.sourceLabelEn }
            : item;
        });
        gridStateHandlers.setData(updatedData);
        setDataChangedEvent((prev) => prev + 1);
      } else if (formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.ADD) {
        const newData = formStateHandlers.getData() as U3121ProjectContactWithSource;
        newData.id = generateTemporaryId();
        newData.source = U3121_PROJECT_CONTACT_SOURCE_PROJECT.source;
        newData.sourceLabelFr = U3121_PROJECT_CONTACT_SOURCE_PROJECT.sourceLabelFr;
        newData.sourceLabelEn = U3121_PROJECT_CONTACT_SOURCE_PROJECT.sourceLabelEn;
        gridStateHandlers.setData([...gridStateHandlers.getData(), newData]);
        gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);
        setDataChangedEvent((prev) => prev + 1);
      }
      formStateHandlers.afterSaveData(formStateHandlers.getData());
    }
    setDialogOpen(false);
  };

  const onDlgDeleteClose = (result: string): void => {
    if (result === "yes") {
      const selectedRows = gridStateHandlers.getSelectedRows();
      const remainingRows = gridStateHandlers.getData().filter((row) => !selectedRows.some((selected) => selected.id === row.id));
      gridStateHandlers.setData(remainingRows);
      gridStateHandlers.setTotalRows(remainingRows.length);
      setDataChangedEvent((prev) => prev + 1);
    }
    setDeleteDialogOpen(false);
  };

  return {
    onAdd,
    onModify,
    onDelete,
    onRowClick,
    onDlgClose,
    isDialogOpen,
    setDialogOpen,
    formStateHandlers,
    gridStateHandlers,
    isDeleteDialogOpen: isDeleteDialogOpen,
    onDlgDeleteClose,
    dataChangedEvent,
  };
}
